/** @jsxImportSource @emotion/react */
import { ReactNode, useRef, Fragment, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';

import usePreventBodyScroll from '../../../hooks/usePreventBodyScroll';
import useResetScroll from '../../../hooks/useResetScroll';
import Icon from '../../Icon';
import Button from '../../Button';
import { ButtonColorType } from '../../Button/Button';

import * as styles from './Dialog.style';
import { IS_SSR } from '../../../../../shared/constants/common';

type DialogSizeType = '400' | '500' | '700';
export const DialogSizes: DialogSizeType[] = ['400', '500', '700'];
export const DialogTypes: ButtonColorType[] = ['primary', 'secondary', 'gray', 'outline'];

export interface PropsType {
  className?: string;
  children: ReactNode;
  isOpen: boolean;
  toggle: (isOpen: boolean) => void;
  onCloseClick?: (event: React.MouseEvent<Element, MouseEvent>) => void;
  confirmButtonContent?: ReactNode;
  confirmButtonType?: ButtonColorType;
  optionButtonContent?: ReactNode;
  optionButtonType?: ButtonColorType;
  titleText?: string;
  size?: '400' | '500' | '700';
  height?: 'auto' | number;
  titleActionText?: string;
  onTitleActionClick?: () => void;
  onConfirmClick?: () => void;
  onOptionClick?: () => void;
  additionalTitleText?: string;
  additionalContentText?: string;
  disabled?: boolean;
  loading?: boolean;
  optionDisabled?: boolean;
}

const Dialog = forwardRef<HTMLDivElement, PropsType>(
  (
    {
      className,
      children,
      isOpen,
      toggle,
      onCloseClick,
      size = '400',
      height = 'auto',
      confirmButtonContent,
      confirmButtonType = 'primary',
      optionButtonContent,
      optionButtonType = 'gray',
      titleText,
      titleActionText,
      onConfirmClick,
      onOptionClick,
      onTitleActionClick,
      additionalTitleText,
      additionalContentText,
      disabled = false,
      loading = false,
      optionDisabled = false,
    },
    ref
  ) => {
    const contentRef = useRef<HTMLDivElement>(null);

    const hasFooter =
      additionalTitleText || additionalContentText || optionButtonContent || confirmButtonContent;

    useImperativeHandle(ref, () => contentRef.current as HTMLDivElement, [isOpen]);
    useResetScroll(contentRef, isOpen);
    usePreventBodyScroll(isOpen);

    function handleClosableClick(event: React.MouseEvent<Element, MouseEvent>) {
      onCloseClick && onCloseClick(event);
      toggle && toggle(false);
    }

    function handleConfirmClick() {
      onConfirmClick && onConfirmClick();
    }

    function handleOptionClick() {
      onOptionClick && onOptionClick();
    }

    function handleTitleActionClick() {
      onTitleActionClick && onTitleActionClick();
    }

    const dialog = (
      <Fragment>
        <div
          role="dialog"
          css={[styles.backdrop, isOpen ? styles.backdropShow : styles.backdropHide]}
          onClick={handleClosableClick}
        />
        <div
          className={className}
          css={[styles.root(size), isOpen ? styles.dialogShow : styles.dialogHide]}
        >
          <div css={[styles.title, !titleText && styles.noTitle]} className="mrt-dialog-title">
            {titleText && <span css={styles.titleContent}>{titleText}</span>}
            <span css={styles.titleAction}>
              {titleActionText && (
                <span onClick={handleTitleActionClick} css={styles.titleActionText}>
                  {titleActionText}
                </span>
              )}
              <Icon
                name="ic_close_md_gray_500"
                alt="close"
                onClick={(event) => {
                  handleClosableClick(event);
                }}
              />
            </span>
          </div>
          <div
            className="mrt-dialog-content"
            ref={contentRef}
            css={[styles.content(height), !titleText && styles.noTitleContent]}
          >
            {children}
          </div>
          {hasFooter && (
            <div css={styles.footer}>
              <div css={styles.additionalInfo}>
                <span css={styles.additionalTitle}>{additionalTitleText}</span>
                <span css={styles.additionalContent}>{additionalContentText}</span>
              </div>
              <div css={styles.buttonGroup}>
                {/* TODO: 버튼 컴포넌트 import 필요 */}
                {optionButtonContent && (
                  <Button
                    type={optionButtonType}
                    size="medium"
                    onClick={handleOptionClick}
                    disabled={optionDisabled}
                  >
                    {optionButtonContent}
                  </Button>
                )}
                {confirmButtonContent && (
                  <Button
                    type={confirmButtonType}
                    size="medium"
                    onClick={handleConfirmClick}
                    disabled={disabled}
                    loading={loading}
                  >
                    {confirmButtonContent}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </Fragment>
    );

    return IS_SSR ? dialog : createPortal(dialog, document.body);
  }
);

export default Dialog;
